<template>
  <ErrorBoundary v-if="isMounted">
    <div class="fe-w-full fe-spacing" v-if="selectedLayoutType">
      <LayoutSelector
        :layout-type="selectedLayoutType"
        :binding-type="bindingType"
      />
      <ModalRegistry />
      <ToastRegistry />
    </div>
  </ErrorBoundary>
</template>

<script>
  /** Components */
  import LayoutSelector from "@/layouts/QnALayouts/LayoutSelector.vue"
  import ModalRegistry from "@/components/Utilities/ModalRegistry/ModalRegistry.vue"
  import ToastRegistry from "@/components/Utilities/ToastRegistry/ToastRegistry.vue"
  import ErrorBoundary from "@/components/Utilities/ErrorBoundary/ErrorBoundary.vue"

  /** Other Items */
  import {
    apiGetProductQnA,
    apiGetVerifiedPurchase,
  } from "@/api_service/qnaApi"
  import { apiGetWidgetConfig } from "@/api_service/configApi"
  import {
    isEmpty,
    checkIsPlatform,
    getDerivedProductValues,
  } from "@/utils/utils"
  import { BINDING_TYPE, CURRENT_VERTICAL } from "@/constants/constants"

  const initialState = {
    // Although we have util method to identify current vertical, but in case if we ever need it in state also, so keeping it here.
    currentVertical: CURRENT_VERTICAL,
    // qnA
    qnA: [],
    qnaPageMeta: {
      loading: false,
      current: 0,
      size: 6,
      hasNext: false,
    },

    // All the derived product values
    product: {},

    // ALl required user data
    user: {},

    // config related to QnA (Either will be fetched from api or sent as prop from Fynd Platform)
    widgetConfig: null,
  }

  export default {
    name: "QuestionAndAnswerWidget",
    components: {
      LayoutSelector,
      ModalRegistry,
      ToastRegistry,
      ErrorBoundary,
    },
    props: {
      config: {
        // This prop is used only when this component is running inside Platform Preview
        type: Object,
        default: () => ({}),
        required: false,
      },
      //
      bindingType: {
        type: String,
        default: BINDING_TYPE.QUESTION_AND_ANSWER,
      },
    },
    provide() {
      return {
        globalState: this.state,
        getProductQnA: this.getProductQnA,
        checkVerifiedPurchase: this.checkVerifiedPurchase,
      }
    },
    data() {
      return {
        state: initialState,
        isFirstMounted: true,
        isMounted: false,
      }
    },
    watch: {
      config() {
        if (this.isPlatform) {
          this.state.widgetConfig = this.config.data
          return
        }
      },
    },
    computed: {
      selectedLayoutType() {
        return this.state.widgetConfig?.layout?.selectedLayout || "layout_one"
      },
      isPlatform() {
        return checkIsPlatform()
      },
    },
    mounted() {
      this.isMounted = true

      this.subscribeToFpiProductState("subscribe")
      this.subscribeToFpiUserState("subscribe")
    },
    destroy() {
      this.subscribeToFpiProductState("unsubscribe")
      this.subscribeToFpiUserState("unsubscribe")
    },
    methods: {
      subscribeToFpiProductState(type) {
        if (type === "subscribe") {
          window.FPI?.state?.product?.subscribe(
            this.setProductDataAndInitializeAPIs.bind(this),
          )
        }

        if (type === "unsubscribe") {
          window.FPI?.state?.product?.unsubscribe(
            this.setProductDataAndInitializeAPIs.bind(this),
          )
        }
      },

      subscribeToFpiUserState(type) {
        if (type === "subscribe") {
          window.FPI?.state?.user?.subscribe(this.setUserData.bind(this))
        }

        if (type === "unsubscribe") {
          window.FPI?.state?.user?.unsubscribe(this.setUserData.bind(this))
        }
      },

      setUserData(user) {
        const userData = user?.user
        this.state.user = {
          user_id: userData?.user_id,
        }
      },

      setProductDataAndInitializeAPIs(product) {
        const productData = getDerivedProductValues(product?.product)

        // User has not moved to new product, so no need to update the data
        if (this.state.product?.productId === productData?.productId) {
          return
        }

        // Set derived product data in product object
        this.state.product = productData

        this.initializeAPIs()
      },

      initializeAPIs() {
        this.resetComponentData()
        this.getWidgetConfig()

        this.getProductQnA()
      },

      resetComponentData() {
        // No need to reset the state on the first run
        if (!this.isFirstMounted) {
          this.isFirstRun = false

          // We should not reset the product Data, except that, we will reset everything back to initial state
          this.state = {
            ...initialState,
            product: this.state?.product || {},
          }
        }
      },

      async getWidgetConfig() {
        // If component is running on Fynd Platform, Then no need to fetch config, because it will be coming from Fynd Platform as prop
        if (this.isPlatform) {
          return
        }

        try {
          const configToFetch = {
            configIdentifierList: [
              { type: "vertical_config", subtype: "question_properties" },
              { type: "vertical_config", subtype: "answer_properties" },
            ],
          }

          const response = await apiGetWidgetConfig(configToFetch)

          if (!isEmpty(response.data)) {
            //TODO - Need to write comment of the response structure
            // Converting Array of object to Object with key - Subtype
            const widgetConfig = response.data?.reduce((config, item) => {
              config[item.subtype] = item.data
              return config
            }, {})

            this.state.widgetConfig = widgetConfig
          }
        } catch (error) {
          console.log(error.message)
        }
      },

      async getProductQnA(pageNoToBeFetched = 0, pagination = false) {
        if (this.state.qnaPageMeta?.loading) {
          return
        }

        try {
          this.state.qnaPageMeta.loading = true

          const query = {
            pageNo: pageNoToBeFetched,
            pageSize: this.state.qnaPageMeta?.size,
          }

          const response = await apiGetProductQnA(
            this.state.product.productId,
            query,
          )

          if (!isEmpty(response.data)) {
            const { data } = response

            if (data.qnA.length) {
              if (pagination) {
                this.state.qnA = data.qnA
              } else {
                this.state.qnA = [...this.state.qnA, ...data.qnA]
              }
            }

            this.state.qnaPageMeta = {
              ...this.state.qnaPageMeta,
              ...data?.page,
            }
            this.state.qnaPageMeta.loading = false
          }
        } catch (error) {
          this.state.qnaPageMeta.loading = false
          console.log("get product qna", error)
        }
      },

      async checkVerifiedPurchase() {
        try {
          const articleId = this.state.product?.alternateCode1
          const response = await apiGetVerifiedPurchase(articleId)

          return response.data?.answerAllowed
        } catch (error) {
          console.log(error)
        }
      },
    },
  }
</script>

<style lang="scss">
  @import "../../assets/styles/styles.scss";
</style>
